import { z } from "zod";

// Field types supported by library field definitions
export const FieldType = z.enum(["text", "user", "date"]);
export type FieldType = z.infer<typeof FieldType>;

// Field definition schema - defines a custom field within a library type
export const FieldDefinitionSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  workspaceId: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable().optional(),
  fieldType: FieldType,
  fieldConfig: z.record(z.unknown()).default({}),
  isRequired: z.boolean().default(false),
  displayOrder: z.number().default(0),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type FieldDefinition = z.infer<typeof FieldDefinitionSchema>;

// Field Group schema - represents a library type (e.g., Risks, Systems, Processes)
export const FieldGroupSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string(),
  name: z.string(), // Display name (e.g., "Risks", "Systems")
  slugPrefix: z.string(), // Prefix for instance slugs (e.g., "RPT" for RPT-001) - required
  description: z.string().nullable().optional(),
  isArchived: z.boolean().default(false),
  displayOrder: z.number().default(0),
  fieldDefinitions: z.array(FieldDefinitionSchema).optional().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type FieldGroup = z.infer<typeof FieldGroupSchema>;

// Wrapped field value format from API: {fieldType: value}
export const WrappedFieldValueSchema = z.union([
  z.object({ text: z.string() }),
  z.object({ userIds: z.array(z.string()) }),
  z.object({ datetime: z.string() }),
]);

export type WrappedFieldValue = z.infer<typeof WrappedFieldValueSchema>;

// Field instance value - the value entry in the values array from API
export const FieldInstanceValueSchema = z.object({
  id: z.string().uuid(),
  instanceId: z.string().uuid(),
  controlId: z.string().uuid().nullable().optional(),
  fieldDefinition: FieldDefinitionSchema,
  value: WrappedFieldValueSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type FieldInstanceValue = z.infer<typeof FieldInstanceValueSchema>;

// Field Group Instance schema - supports both hydrated and non-hydrated formats
export const FieldGroupInstanceSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string(),
  slugSuffix: z.string(), // Unique suffix within the group (e.g., "001")
  name: z.string(), // Display name (e.g., "Unauthorized Access")
  description: z.string().nullable().optional(),
  // Hydrated fields (from list API)
  group: FieldGroupSchema.optional(), // Full group object (hydrated)
  values: z.array(FieldInstanceValueSchema).optional().default([]), // Field values array (hydrated)
  slug: z.string().optional(), // Computed: {group.slugPrefix}-{slugSuffix} (hydrated)
  // Non-hydrated field (for backward compat)
  groupId: z.string().uuid().optional(), // Reference to field group (non-hydrated)
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type FieldGroupInstance = z.infer<typeof FieldGroupInstanceSchema>;

// Alias for backward compatibility
export const HydratedFieldGroupInstanceSchema = FieldGroupInstanceSchema;
export type HydratedFieldGroupInstance = FieldGroupInstance;

// StandaloneCustomField - API response for control custom fields
export const StandaloneCustomFieldSchema = z.object({
  id: z.string().uuid(),
  controlId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  fieldType: FieldType,
  fieldConfig: z.record(z.unknown()).default({}),
  isRequired: z.boolean().default(false),
  displayOrder: z.number().default(0),
  value: WrappedFieldValueSchema.nullable().optional(),
  valueId: z.string().uuid().nullable().optional(),
  linkId: z.string().uuid().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type StandaloneCustomField = z.infer<typeof StandaloneCustomFieldSchema>;

// Request schemas for control custom fields
export const CreateCustomFieldRequestSchema = z.object({
  name: z.string(),
  field_type: FieldType.default("text"),
  field_config: z.record(z.unknown()).optional().default({}),
  value: WrappedFieldValueSchema.nullable().optional(),
  display_order: z.number().optional().default(0),
});

export type CreateCustomFieldRequest = z.infer<
  typeof CreateCustomFieldRequestSchema
>;

export const UpdateCustomFieldRequestSchema = z.object({
  name: z.string().optional(),
  field_config: z.record(z.unknown()).optional(),
  value: WrappedFieldValueSchema.nullable().optional(),
  display_order: z.number().optional(),
});

export type UpdateCustomFieldRequest = z.infer<
  typeof UpdateCustomFieldRequestSchema
>;

// Control Relationship schema - links a control to a library instance
// API response uses "instance" which contains the full FieldGroupInstance with nested "group"
export const ControlRelationshipSchema = z.object({
  id: z.string().uuid(),
  controlId: z.string().uuid(),
  createdAt: z.string(),
  // The hydrated instance data from API (contains nested group as instance.group)
  instance: HydratedFieldGroupInstanceSchema.optional().nullable(),
});

export type ControlRelationship = z.infer<typeof ControlRelationshipSchema>;

// Response schema for listing field groups
export const FieldGroupsResponseSchema = z.object({
  fieldGroups: z.array(FieldGroupSchema),
});

// Response schema for listing field group instances
export const FieldGroupInstancesResponseSchema = z.object({
  instances: z.array(FieldGroupInstanceSchema),
  totalCount: z.number().optional(),
});

// Response schema for control relationships
export const ControlRelationshipsResponseSchema = z.object({
  relationships: z.array(ControlRelationshipSchema),
});

// Request schema for creating a field group instance
export const CreateFieldGroupInstanceRequestSchema = z.object({
  groupId: z.string().uuid(),
  slugSuffix: z.string().optional(), // Optional - auto-generated if omitted (e.g., "001", "002")
  name: z.string(),
  description: z.string().optional().nullable(),
  fieldValues: z
    .record(z.string(), WrappedFieldValueSchema)
    .optional()
    .nullable(),
});

export type CreateFieldGroupInstanceRequest = z.infer<
  typeof CreateFieldGroupInstanceRequestSchema
>;

// Request schema for updating a field group instance
export const UpdateFieldGroupInstanceRequestSchema = z.object({
  slugSuffix: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional().nullable(),
  fieldValues: z
    .record(z.string(), WrappedFieldValueSchema)
    .optional()
    .nullable(),
});

export type UpdateFieldGroupInstanceRequest = z.infer<
  typeof UpdateFieldGroupInstanceRequestSchema
>;

// Request schema for creating a field group (library type)
export const CreateFieldGroupRequestSchema = z.object({
  name: z.string(),
  slugPrefix: z.string(), // Required - prefix for instance slugs (e.g., "RPT")
  description: z.string().optional().nullable(),
  displayOrder: z.number().optional(),
});

export type CreateFieldGroupRequest = z.infer<
  typeof CreateFieldGroupRequestSchema
>;

// Request schema for updating a field group (library type)
export const UpdateFieldGroupRequestSchema = z.object({
  name: z.string().optional(),
  slugPrefix: z.string().optional(),
  description: z.string().optional().nullable(),
  displayOrder: z.number().optional(),
});

export type UpdateFieldGroupRequest = z.infer<
  typeof UpdateFieldGroupRequestSchema
>;

// Utility function to compute full slug from prefix and suffix
export function computeFullSlug(
  slugPrefix: string,
  slugSuffix: string,
): string {
  return `${slugPrefix}-${slugSuffix}`;
}

// Helper to get display slug from instance
export function getInstanceDisplaySlug(
  instance: FieldGroupInstance,
  fieldGroup?: FieldGroup | null,
): string {
  // Use the computed slug from API
  if (instance.slug) {
    return instance.slug;
  }
  // Compute from group if available
  if (instance.group) {
    return computeFullSlug(instance.group.slugPrefix, instance.slugSuffix);
  }
  // If group is passed separately
  if (fieldGroup) {
    return computeFullSlug(fieldGroup.slugPrefix, instance.slugSuffix);
  }
  // Fallback to just the suffix
  return instance.slugSuffix;
}

// Helper to get field value from instance values array
export function getFieldValue(
  instance: FieldGroupInstance,
  fieldDefinitionId: string,
): WrappedFieldValue | null {
  const valueEntry = instance.values?.find(
    (v) => v.fieldDefinition.id === fieldDefinitionId,
  );
  return valueEntry?.value ?? null;
}

// Helper to convert values array to fieldValues record for forms
export function valuesToFieldValuesRecord(
  values: FieldInstanceValue[] | undefined,
): Record<string, WrappedFieldValue> {
  const record: Record<string, WrappedFieldValue> = {};
  if (!values) return record;
  for (const v of values) {
    record[v.fieldDefinition.id] = v.value;
  }
  return record;
}
